import { defineStore } from 'pinia'
import { getMaxAssortNum, getMaxQty, getMinQty, getPriceList, getSpecListByPriceList, useWindow } from '../../composables'
import { OrderType, type PriceInfo } from '../../types'
import { useAddon } from './addon'
import { useImprintMethod } from './imprintMethod'
import { useProductSpec } from './productSpec'
import { useSpec } from './spec'
import { type ICustomizeEventInfo } from '@anypromo/shared/types/SaleCustomize'

interface AppState {
  // 是否为blank
  isBlank: boolean
  // 最大支持的assorted数量
  maxAssortNum: number
  // 最小购买数量
  minQty: number
  // 最大购买数量
  maxQty: number
  // imprint 支持情况， 1: 支持imprint、 2：支持blank，3：支持imprint和blank
  blankLimitation: number
  // 当前的price list
  currentPriceList: PriceInfo[]

  // 是否为gift with purchase订单
  isFreeGift: boolean
  // 如果是GWP商品，是否编辑过
  // isFreeGiftCustomized:boolean
  //
  transitTime: number
  // 订单类型
  orderType: OrderType
  // samePreviousChecked
  samePreviousChecked: boolean
  // samePreviousDisaled
  samePreviousDisaled: boolean

  // 最大max location数量
  maxLocationNum: number
  couponCode: string
  customizeEventName: string
  customizeEventDate: string
  customizeEventList: Array<ICustomizeEventInfo>
}

export const useApp = defineStore('app', {
  state: (): AppState => {
    return {
      isBlank: false,
      maxAssortNum: 1,
      blankLimitation: 1,
      maxLocationNum: 99,
      minQty: 0,
      maxQty: 0,
      currentPriceList: [],
      transitTime: 5,
      isFreeGift: false,
      // isFreeGiftCustomized :false,
      samePreviousChecked: false,
      samePreviousDisaled: false,
      orderType: OrderType.Normal,
      couponCode: '',
      customizeEventName: '',
      customizeEventDate: '',
      customizeEventList: [],
    }
  },
  getters: {
    getBlankValue (): boolean {
      return this.isBlank
    },
    getOrderType (): OrderType {
      return this.orderType
    },
    getIsFreeGift (): boolean {
      return this.isFreeGift
    },
    getBlankLimitation (): number {
      return this.blankLimitation
    },
    getCouponCode (): string {
      return this.couponCode
    },
    getCustomizeEventName (): string {
      return this.customizeEventName
    },
    getCustomizeEventDate (): string {
      return this.customizeEventDate
    },
    getCustomizeEventList (): Array<ICustomizeEventInfo> {
      return this.customizeEventList
    },
    getMaxAssortNum (): number {
      return this.maxAssortNum
    },

    getMinQty (): number {
      return this.minQty
    },

    getMaxQty (): number {
      return this.maxQty
    },
    getTransitTime (): number {
      return this.transitTime
    },
    getCurrentPriceList (): PriceInfo[] {
      return this.currentPriceList
    }
  },
  actions: {
    dataFill (state: Partial<AppState>) {
      this.$patch(state)
    },
    setBlankValue (val: boolean) {
      this.isBlank = val
    },
    setMaxAssortNum (val: number) {
      this.maxAssortNum = val
    },
    setSamePreviousChecked (val: boolean) {
      this.samePreviousChecked = val
    },
    setSamePreviousDisaled (val: boolean) {
      this.samePreviousDisaled = val
    },
    setCurrentPriceList (values: PriceInfo[]) {
      this.currentPriceList.splice(0)
      this.currentPriceList.push(...values)
    },
    updatePriceList (): void {
      const productSpecState = useProductSpec()
      const specState = useSpec()
      const addonState = useAddon()
      const imprintMethodState = useImprintMethod()
      const {
        productSpecList,
      } = useWindow()

      const priceList = getPriceList({
        isBlank: this.getBlankValue,
        productSpecList: productSpecState.getSelectedList,
        specList: specState.getSelectedList,
        addonList: addonState.getSelectedList,
        imprintMethodList: imprintMethodState.getSelectedList
      });

      this.setCurrentPriceList(priceList)

      // 计算最大可用location数量
      const maxLocationNum = Math.min(Math.max(1, ...priceList.map((f) => f.ImprintLocationNum)), 999)

      // maxLocationNum
      this.maxLocationNum = maxLocationNum

      // 支持最大assort 数量
      const maxAssortNum = getMaxAssortNum(
        getSpecListByPriceList(priceList, productSpecList, [], [])
      )

      this.setMaxAssortNum(maxAssortNum);

      //计算minqty
      let minQty = 0;
      if (this.isBlank) {
        minQty = getMinQty(priceList);
      } else {
        minQty = getMinQty(priceList, imprintMethodState.getSelectedList)
      }
      this.maxQty = getMaxQty(priceList);
      this.minQty = minQty;
    }
  }
})
